'use client';

import { useProductPriceWeightDisplay } from '@/components/product-utils';
import { Swatch, SwatchGroup } from '@/components/swatch-group';
import { useVariationAttributes } from '@/hooks/product/use-variation-attributes';
import { toImageUrl } from '@/lib/images/dynamic-image';
import { getGiftCardAmountPrice, isGiftCardProduct, GIFT_CARD_SIZE_ATTRIBUTE_ID } from '@/lib/gift-card-product-utils';
import {
    buildMultipackOptions,
    formatMultipackWeightText,
    getMultipackContentProduct,
    getMultipackPackQty,
    hasMultiPackBundles,
} from '@/lib/multipack';
import { isProductSet } from '@/lib/product-utils';
import { useProductView } from '@/providers/product-view';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { ShopperProducts } from '@/scapi';
import { type ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import ProductPrice from '../product-price-custom';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import GiftCardAmountSelector from './gift-card-amount-selector';
import MultipackSelector from './multipack-selector';
import ProductGiftOption from './product-gift/product-gift-option';
import WineVintageSelector, { WINE_VINTAGE_YEAR_ATTRIBUTE_ID } from './wine-vintage-selector';
import { isWineProduct } from '@/lib/wine-vintage';
import { cn } from 'src/lib/utils';

type ProductInfoBaseProps = {
    product: ShopperProducts.schemas['Product'];
    hideVariantSelection?: boolean;
    /** Layout style: 'full' (default) shows title, description, inventory; 'compact' shows brand, smaller title, sorted attributes */
    variantStyle?: 'full' | 'compact';
    /** When true and mode is 'edit', show quantity picker (e.g. in cart edit modal) */
    showQuantityInEditMode?: boolean;
};
type ProductInfoUncontrolledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'uncontrolled' uses URL navigation */
    swatchMode?: 'uncontrolled';
    onAttributeChange?: never;
    variationValues?: never;
};
type ProductInfoControlledProps = ProductInfoBaseProps & {
    /** Mode for swatch interaction: 'controlled' uses callback */
    swatchMode: 'controlled';
    /** Callback when variant attribute changes in controlled mode */
    onAttributeChange: (attributeId: string, value: string) => void;
    /** Controlled variation values for controlled mode (e.g., {color: 'red', size: 'M'}) */
    variationValues: { [key: string]: string };
};
type ProductInfoProps = ProductInfoUncontrolledProps | ProductInfoControlledProps;
/**
 * ProductInfo component displays product details including title, description, price, variants, and quantity picker
 *
 * Supports two swatch modes:
 * - uncontrolled mode (default): Swatches use URL navigation for variant selection
 * - controlled mode: Swatches use callbacks for controlled variant selection (used in modals)
 *
 * @param props - Component props
 * @param props.product - The product data to display
 * @param props.swatchMode - Swatch interaction mode ('uncontrolled' or 'controlled')
 * @param props.onAttributeChange - Callback for controlled mode variant changes
 * @param props.variationValues - Controlled variation values for controlled mode
 * @returns JSX element with product information display
 */
export default function ProductInfoPrice({
    product,
    swatchMode = 'uncontrolled',
    onAttributeChange,
    variationValues,
    hideVariantSelection = false,
    variantStyle = 'full',
}: ProductInfoProps): ReactElement {
    const config = useConfig();
    const isProductASet = isProductSet(product);
    const isMultipack = hasMultiPackBundles(product);
    const multipackOptions = isMultipack ? buildMultipackOptions(product) : null;
    const isGiftCard = isGiftCardProduct(product);
    const isWine = isWineProduct(product);
    // Use variation attributes hook for URL-aware swatches
    const variationAttributes = useVariationAttributes({ product });
    // Get currency from context (automatically derived from locale)
    const { currency } = useSite();
    // Get shared state from context
    const { quantity, currentVariant } = useProductView();
    const [, setSearchParams] = useSearchParams();

    const { t } = useTranslation('product');
    const contentProduct = getMultipackContentProduct(product);
    const packQty = getMultipackPackQty(product);
    const { unitRow, perUnitLine, pricePct, isOnSale, omnibus } = useProductPriceWeightDisplay(
        contentProduct,
        currency,
        {
            priceProduct: isMultipack ? product : undefined,
            packQty: isMultipack ? packQty : undefined,
        }
    );
    const weightText = formatMultipackWeightText(unitRow.weightText, isMultipack ? packQty : 1);
    const showPriceWeightUnit = Boolean(weightText || perUnitLine);
    const isCompactStyle = variantStyle === 'compact';

    const giftCardSizeAttribute = variationAttributes.find((attribute) => attribute.id === GIFT_CARD_SIZE_ATTRIBUTE_ID);

    const setGiftCardSize = useCallback(
        (value: string) => {
            setSearchParams(
                (prev) => {
                    const nextParams = new URLSearchParams(prev);
                    nextParams.set(GIFT_CARD_SIZE_ATTRIBUTE_ID, value);
                    nextParams.delete('pid');
                    return nextParams;
                },
                { replace: true, preventScrollReset: true }
            );
        },
        [setSearchParams]
    );

    if (isGiftCard) {
        const selectedCutValue =
            swatchMode === 'controlled'
                ? variationValues?.[GIFT_CARD_SIZE_ATTRIBUTE_ID]
                : giftCardSizeAttribute?.selectedValue?.value;

        // When the loader has already resolved the cut SKU, `product.price` is the selected cut.
        // While switching cuts (or on master payloads), derive price from the matching variants[] entry.
        const selectedCutPrice =
            selectedCutValue != null
                ? (getGiftCardAmountPrice(product, selectedCutValue) ?? currentVariant?.price ?? product.price)
                : (currentVariant?.price ?? product.price);
        const priceProduct =
            selectedCutPrice != null
                ? ({
                      ...product,
                      price: selectedCutPrice,
                      priceMax: selectedCutPrice,
                      hitType: undefined,
                      type: { variant: true },
                      variants: undefined,
                  } as ShopperProducts.schemas['Product'])
                : product;

        return (
            <div className="relative grid gap-6">
                {giftCardSizeAttribute ? (
                    <GiftCardAmountSelector
                        values={giftCardSizeAttribute.values}
                        product={product}
                        currency={currency}
                        selectedValue={selectedCutValue}
                        readOnly={hideVariantSelection}
                        onSelect={
                            hideVariantSelection
                                ? undefined
                                : swatchMode === 'controlled'
                                  ? (value) => onAttributeChange?.(GIFT_CARD_SIZE_ATTRIBUTE_ID, value)
                                  : setGiftCardSize
                        }
                    />
                ) : null}
                <ProductGiftOption product={product} />
                <ProductPrice
                    type="unit"
                    product={priceProduct}
                    quantity={quantity}
                    currency={currency}
                    labelForA11y={product?.name}
                    currentPriceOnly
                    currentPriceProps={{
                        className: 'heading-title-l mt-0',
                    }}
                    hidePromo
                    showPriceWeightUnit={false}
                />
                {isProductASet && (
                    <div className="bg-primary/10 border border-primary rounded-lg p-4">
                        <p className="text-sm text-primary">{t('productSetNotice')}</p>
                    </div>
                )}
            </div>
        );
    }

    // In compact mode, sort variation attributes by priority order
    const COMPACT_ATTRIBUTE_ORDER = ['size', 'color'];
    const sortedVariationAttributes = isCompactStyle
        ? [...variationAttributes].sort((a, b) => {
              const aIndex = COMPACT_ATTRIBUTE_ORDER.indexOf(a.id);
              const bIndex = COMPACT_ATTRIBUTE_ORDER.indexOf(b.id);
              // Attributes not in the list sort to the end, preserving original order
              const aPriority = aIndex === -1 ? COMPACT_ATTRIBUTE_ORDER.length : aIndex;
              const bPriority = bIndex === -1 ? COMPACT_ATTRIBUTE_ORDER.length : bIndex;
              return aPriority - bPriority;
          })
        : variationAttributes;

    return (
        <div className={cn('relative grid gap-4', (isWine || isMultipack) && 'gap-8')}>
            {multipackOptions ? <MultipackSelector options={multipackOptions} currency={currency} /> : null}
            {/* Price - show unit price on PDP */}
            <div className={cn('space-y-3', (isWine || isMultipack) && 'order-last')}>
                <ProductPrice
                    type="unit"
                    product={product}
                    quantity={quantity}
                    currency={currency}
                    labelForA11y={product?.name}
                    currentPriceProps={{
                        className: `heading-title-l mt-0`,
                    }}
                    highlightOnSaleCurrentPrice={isOnSale}
                    listPriceProps={{
                        className: 'body-copy-l-bold font-bold text-[var(--neutral-10)] line-through',
                    }}
                    promoCalloutProps={{
                        className:
                            'label-s bg-[var(--status-red-40)] w-fit px-1 py-[1px] rounded-sm text-[var(--neutral-white)]',
                    }}
                    hidePromo={isCompactStyle}
                    currentPriceOnly={isCompactStyle}
                    showPriceWeightUnit={showPriceWeightUnit}
                    pricePct={pricePct}
                    unitRow={{ weightText: weightText ?? '', perUnitText: perUnitLine ?? undefined }}
                    omnibus={omnibus}
                />
            </div>
            {/* Swatch Groups for Product Variations */}
            {sortedVariationAttributes.map(({ id, name, selectedValue, values }) => {
                // In controlled mode, derive display name from variationValues state
                const controlledValue = variationValues?.[id];
                const controlledDisplayName = controlledValue
                    ? values.find((v) => v.value === controlledValue)?.name || ''
                    : '';

                if (id === WINE_VINTAGE_YEAR_ATTRIBUTE_ID) {
                    return (
                        <WineVintageSelector
                            key={id}
                            values={values}
                            label={name}
                            selectedValue={swatchMode === 'controlled' ? controlledValue : selectedValue?.value}
                            readOnly={hideVariantSelection}
                            onSelect={
                                hideVariantSelection
                                    ? undefined
                                    : swatchMode === 'controlled'
                                      ? (value) => onAttributeChange?.(id, value)
                                      : undefined
                            }
                        />
                    );
                }

                // When hideVariantSelection is true, only show the selected swatch (read-only)
                const swatchesToShow = hideVariantSelection
                    ? values.filter((v) => v.value === selectedValue?.value)
                    : values;

                const swatches = swatchesToShow.map((value) => {
                    const { href, name: valueName, image, value: swatchValue, orderable } = value;
                    const swatchImageUrl = (image && toImageUrl({ image, config })) || '';
                    const content = image ? (
                        <>
                            <span
                                className="rounded-pill bg-cover bg-center bg-no-repeat"
                                style={{
                                    width: 'var(--swatch-color-dot, 100%)',
                                    height: 'var(--swatch-color-dot, 100%)',
                                    backgroundColor: valueName?.toLowerCase(),
                                    backgroundImage: swatchImageUrl ? `url(${swatchImageUrl})` : undefined,
                                    border: 'var(--swatch-color-dot-border, none)',
                                }}
                                aria-label={image.alt || valueName}
                            />
                            <span
                                className="text-xs font-medium capitalize ml-1"
                                style={{ display: 'var(--swatch-color-label)' }}>
                                {valueName}
                            </span>
                        </>
                    ) : (
                        <span className="text-xs font-medium">{valueName}</span>
                    );

                    return (
                        <Swatch
                            key={swatchValue}
                            href={swatchMode === 'uncontrolled' ? href : undefined}
                            // Disable when not orderable (out of stock)
                            disabled={!orderable}
                            value={swatchValue}
                            name={valueName}
                            shape={id === 'color' ? 'color' : 'label'}
                            labeled>
                            {content}
                        </Swatch>
                    );
                });
                return (
                    <SwatchGroup
                        key={id}
                        value={swatchMode === 'uncontrolled' ? selectedValue?.value : controlledValue}
                        displayName={swatchMode === 'controlled' ? controlledDisplayName : selectedValue?.name || ''}
                        label={name}
                        handleChange={
                            // Disable handleChange when hideVariantSelection is true
                            hideVariantSelection
                                ? undefined
                                : swatchMode === 'controlled'
                                  ? (value) => onAttributeChange?.(id, value)
                                  : undefined
                        }>
                        {swatches}
                    </SwatchGroup>
                );
            })}
            {/* Product set notice */}
            {isProductASet && (
                <div className="bg-primary/10 border border-primary rounded-lg p-4">
                    <p className="text-sm text-primary">{t('productSetNotice')}</p>
                </div>
            )}
        </div>
    );
}
